<template>
	<div class="height-full">
		<DialogHeader closable @cancel="$emit('close')" />
		<DialogBody class="invitation-body gap-5">
			<div class="d-flex flex-column gap-8 mt-8">
				<div class="d-flex flex-column gap-4 rounded-2">
					<div class="d-flex flex-column gap-2">
						<h1 class="text-title-m">
							{{
								isOnboarding ? $t('app.trip.onboarding.invite.title') : $t('web.trip.invite.title')
							}}
						</h1>
						<p class="text-body-m text-neutral-700">
							{{
								isOnboarding
									? $t('app.trip.onboarding.invite.description')
									: $t('web.trip.invite.description')
							}}
						</p>
					</div>
					<div class="d-flex flex-column gap-2" :class="isXS ? 'gap-4' : 'gap-2'">
						<div class="d-flex" :class="isXS ? 'flex-column' : 'align-center gap-2'">
							<InputText
								ref="emailInput"
								v-model="email"
								:placeholder="$t('web.trip.invite.email.placeholder')"
								:label="$t('web.trip.invite.email.label')"
								size="s"
								:error="emailError"
							/>
							<PButton :size="isXS ? 'lg' : 'md'" type-filled color-primary @click="sendEmail">
								{{ $t('common.action.invite') }}
							</PButton>
						</div>
						<button
							class="width-fit-content text-body-m cursor-pointer d-flex bg-white text-social-600"
							@click="copyLink"
						>
							<Icon icon="link" />
							{{ $t('web.trip.invite.copy_link') }}
						</button>
					</div>
				</div>
			</div>
			<hr class="divider border-xs d-flex flex-grow-1 border-neutral-200" />
			<div>
				<h2 class="text-label-m">
					{{ $t('web.trip.invite.travelers.title') }}
					<label class="text-body-m">({{ itinerary.numTravelers! }})</label>
				</h2>
				<div
					class="d-flex flex-column gap-3 overflow-auto"
					:class="isXS ? 'travelers-list-mobile' : 'travelers-list'"
				>
					<TripTravelersList
						:travelers="travelersList"
						:itinerary-id="itinerary.id!"
						:is-x-s="isXS"
						@delete="openDeleteTraveler"
					/>
					<TripGuestsList
						v-if="invitations?.length"
						:invitations="invitations"
						:is-x-s="isXS"
						@resend="reSendInvitation"
						@cancel="openCancelInvitation"
					/>
					<TripPotentialGuestsList
						v-if="nonInvitedTravelers > 0"
						:guest-number="nonInvitedTravelers"
						:guest-start="invitedTravelers"
						:is-x-s="isXS"
						@remove="potentialTravelerToDelete = true"
						@invite="focusEmailInput"
					/>
				</div>
			</div>
		</DialogBody>
		<DialogCriticalConfirmation
			:show="Boolean(invitationToCancel)"
			:title="$t('web.trip.invite.modal.remove.title')"
			:description="$t('web.trip.invite.modal.remove.description')"
			:accept-text="$t('web.trip.invite.modal.remove.confirm')"
			:cancel-text="$t('common.action.cancel')"
			@confirm="cancelInvitation"
			@cancel="invitationToCancel = undefined"
		/>
		<DialogCriticalConfirmation
			:show="Boolean(travelerToDelete)"
			:title="$t('web.trip.invite.modal.remove.title')"
			:description="$t('web.trip.invite.modal.remove.description')"
			:accept-text="$t('web.trip.invite.modal.remove.confirm')"
			:cancel-text="$t('common.action.cancel')"
			@confirm="deleteTraveler"
			@cancel="travelerToDelete = undefined"
		/>
		<DialogCriticalConfirmation
			:show="Boolean(potentialTravelerToDelete)"
			:title="$t('web.trip.invite.modal.remove.title')"
			:description="$t('web.trip.invite.modal.remove.description')"
			:accept-text="$t('web.trip.invite.modal.remove.confirm')"
			:cancel-text="$t('common.action.cancel')"
			@confirm="deletePotentialTraveler"
			@cancel="potentialTravelerToDelete = false"
		/>
	</div>
</template>

<script setup lang="ts">
	import { onMounted, ref, computed } from 'vue'
	import { Itinerary2, TRIP_ROLES } from '~/models/Itinerary'
	import { Traveler2 } from '~/models/Traveler'
	import EventsService from '~/services/events'
	import { TranslationService } from '~/services/Translation'
	import { TripTravelerActions } from '~/actions/trip/traveler'
	import TextValidator from '~/passporter-services/shared/textValidator'
	import { TripInvitation } from '~/models/TripInvitation'
	import TripInvitationAction from '~/actions/trip/invitation'
	import { TripTravelerMetric } from '~/actions/metrics/tripTraveler'
	import { ItineraryActions } from '~/actions/trip/itinerary'
	import { useTrip } from '~/composables/trip'

	interface Props {
		itinerary: Itinerary2
		travelers?: Traveler2[]
		isXS: boolean
		isOnboarding?: boolean
	}
	const props = defineProps<Props>()
	defineEmits(['close'])

	const route = useRoute()
	const { t } = useI18n()
	const { trip } = useTrip(props.itinerary.id!)

	const travelersList = ref<Traveler2[]>()
	const email = ref<string>()
	const emailError = ref<string>()
	const invitations = ref<TripInvitation[]>()
	const invitationLink = ref<string>('')

	const invitationToCancel = ref<string>()
	const travelerToDelete = ref<string>()
	const potentialTravelerToDelete = ref<boolean>()

	const userRole = ref<TRIP_ROLES>()
	const potentialTravelers = ref<number>(
		trip.value?.potentialTravelers || props.itinerary?.potentialTravelers || 0
	)
	const emailInput = ref()

	onMounted(() => {
		generateLink()
		getTravelers()
		getInvitations()
		sendViewEvent()
	})

	const sendEmail = async () => {
		if (!email.value || !validateEmail()) return
		sendInvitation(email.value)
	}

	const sendInvitation = async (invitationEmail: string) => {
		try {
			await TripInvitationAction.inviteWithEmail(props.itinerary.id!, invitationEmail)
			getInvitations()
			getTravelers()
			email.value = undefined
			sendAddEmailEvent()
		} catch {
			emailError.value = t('web.trip.invite.email.error_already_invited')
		}
	}

	const reSendInvitation = async (invitationEmail: string) => {
		await TripInvitationAction.resendInvitation(props.itinerary.id!, invitationEmail)
		getInvitations()
		getTravelers()
	}

	const openCancelInvitation = (invitationId: string) => {
		invitationToCancel.value = invitationId
	}

	const cancelInvitation = async () => {
		if (!invitationToCancel.value) return
		await TripInvitationAction.cancelInvitation(props.itinerary.id!, invitationToCancel.value)
		invitationToCancel.value = undefined
		getInvitations()
		getTravelers()
	}

	const openDeleteTraveler = (travelerId: string) => {
		travelerToDelete.value = travelerId
	}

	const deleteTraveler = async () => {
		if (!travelerToDelete.value) return
		await TripTravelerActions.deleteTraveler(props.itinerary.id!, travelerToDelete.value)
		travelersList.value = await TripTravelerActions.getTravelers(props.itinerary.id!)
		travelerToDelete.value = undefined
		EventsService.emitDeleteTraveler()
	}

	const validateEmail = (): boolean => {
		const isValid = Boolean(email.value && TextValidator.email(email.value))
		emailError.value = isValid ? '' : t('web.trip.invite.email.error_invalid')
		return isValid
	}

	const copyLink = async () => {
		await navigator.clipboard.writeText(invitationLink.value)
		EventsService.emitSuccess(
			TranslationService.getInstance().translate('web.trip.invite.toast.copy_link.success')
		)
		sendAddLinkEvent()
	}

	const generateLink = async () => {
		invitationLink.value = await TripTravelerActions.generateInvitationLink(props.itinerary.id!)
	}

	const getTravelers = async () => {
		travelersList.value = await TripTravelerActions.getTravelers(props.itinerary.id!)
	}

	const getInvitations = async () => {
		invitations.value = await TripInvitationAction.getInvitations(props.itinerary.id!)
	}

	const focusEmailInput = () => emailInput.value?.focusInput()

	const deletePotentialTraveler = async () => {
		try {
			await TripInvitationAction.deletePotentialTraveler(props.itinerary.id!)
			if (potentialTravelers.value <= 1) return
			potentialTravelers.value = potentialTravelers.value - 1
			if (!trip.value) return
			trip.value.potentialTravelers = potentialTravelers.value
			potentialTravelerToDelete.value = false
		} catch {
			potentialTravelerToDelete.value = false
		}
	}

	const invitedTravelers = computed(() => {
		const invitationNum = invitations.value?.length || 0
		const travelerNum = travelersList.value?.length || 0
		return invitationNum + travelerNum
	})

	const nonInvitedTravelers = computed(() => {
		const invitationNum = invitations.value?.length || 0
		const travelerNum = travelersList.value?.length || 0
		return potentialTravelers.value - travelerNum - invitationNum
	})

	const sendViewEvent = async () => {
		const routeName: string | undefined = route.name?.toString()
		const destinationName: string = props.itinerary.destination?.standardizedName || ''
		const itineraryId: string = props.itinerary.id!
		const itineraryOptions = await ItineraryActions.getTripOptions(itineraryId)
		userRole.value = itineraryOptions?.role
		TripTravelerMetric.viewInvitationDialog(
			props.itinerary.id!,
			destinationName,
			userRole.value,
			routeName
		)
	}

	const sendAddEmailEvent = async () => {
		const routeName: string | undefined = route.name?.toString()
		const destinationName: string = props.itinerary.destination?.standardizedName || ''
		TripTravelerMetric.addTravelerEmail(
			props.itinerary.id!,
			destinationName,
			userRole.value,
			routeName
		)
	}

	const sendAddLinkEvent = async () => {
		const routeName: string | undefined = route.name?.toString()
		const destinationName: string = props.itinerary.destination?.standardizedName || ''
		TripTravelerMetric.addTravelerLink(
			props.itinerary.id!,
			destinationName,
			userRole.value,
			routeName
		)
	}
</script>

<style lang="scss" scoped>
	.invitation-body {
		overflow: hidden;
	}

	.travelers-list {
		max-height: 215px;
		min-height: 215px;
		&-mobile {
			max-height: calc(100vh - 440px);
		}
	}
</style>
